import { Lightning, Colors, AppData, Router, Registry } from '@lightningjs/sdk';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { PageId } from 'types/pageId';
import { Media } from 'types/api/media';
import { SelectItemContext } from 'types/events';
import { StaticViewContexts, ViewContext } from 'types/analytics';
import { onTileSelectedHandler } from 'support/routerUtils';
import { EnabledWidgets, WidgetNames } from 'config/routes';
import { constants } from 'aliases';
import { ContentHub } from 'services/cwData';
import { translate } from 'support/translate';
import { TopNavSelectedType } from './widgets/TopNav';
import { TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS } from './widgets/TopNav';
import { getImageTextureObj } from 'support/generalUtils';
import {
  getSimulatedKeyEventFromMap,
  isKeySimulated,
} from 'support/keyEventUtils';
import { DirectionsType } from 'support/keyEventUtils';

const LOW_PERFORMANCE_LONG_PRESS_BUFFER = 300;
const PAGE_WIDTH = 1920;
const PAGE_HEIGHT = 1080;

export interface PageTemplateSpec extends Lightning.Component.TemplateSpec {
  loaded: boolean;
  spinnerPosition: { x?: number; y?: number };
  Background: object;
  SponsorBackground: Lightning.textures.ImageTexture;
  LoadingSpinner: typeof LoadingSpinner;
}

interface PageTypeConfig extends Lightning.Component.TypeConfig {
  IsPage: true;
}

interface ChildPageTemplateSpec<TData = unknown>
  extends Lightning.Component.TemplateSpec {
  pageId?: PageId;
  viewContext?: ViewContext;
  pageData?: TData;
}

export default class Page<
  TemplateSpec extends ChildPageTemplateSpec = ChildPageTemplateSpec,
  TypeConfig extends Lightning.Component.TypeConfig = Lightning.Component.TypeConfig,
> extends Lightning.Component<
  PageTemplateSpec & TemplateSpec,
  PageTypeConfig & TypeConfig
> {
  protected _pageId = PageId.UNKNOWN;
  // UNKNOWN view context should be overwritten for all playback events
  protected _viewContext = StaticViewContexts.UNKNOWN;
  protected _handleCcChange: ((ccEnabled: boolean) => void) | undefined;

  private _ccChangeListener: ((tvCcEnabled: boolean) => void) | undefined;

  private _loaded = false;
  private _LoadingSpinner = (this as Page).getByRef('LoadingSpinner')!;
  private _Background = (this as Page).getByRef('Background')!;
  private _SponsorBackground = (this as Page).getByRef('SponsorBackground')!;
  private _pageData: TemplateSpec['pageData'] = undefined;

  private _playerExitTimeout: number | null = null;
  private _upLongPressStartTime: number | null = null;
  private _isTopNavFocusable = true;

  get pageId() {
    return this._pageId;
  }

  get viewContext() {
    return this._viewContext;
  }

  set pageData(content: TemplateSpec['pageData']) {
    this._pageData = content;
  }

  get pageData() {
    return this._pageData;
  }

  set spinnerPosition(value: { x?: number; y?: number }) {
    this._LoadingSpinner.patch({
      x: value?.x,
      y: value?.y,
    });
  }

  set loaded(value: boolean) {
    this._loaded = value;
    this._updateSpinner();
  }

  get loaded() {
    return this._loaded;
  }

  set sponsorBackground(imageUrl: string) {
    this._SponsorBackground.patch({
      ...(imageUrl
        ? getImageTextureObj(imageUrl, PAGE_WIDTH, PAGE_HEIGHT)
        : {}),
      visible: !!imageUrl,
    });
  }

  set backgroundColor(colorName: string) {
    const color = Colors(colorName).get();
    this._Background.patch({ color });
  }

  get isNavBarVisible(): boolean {
    const widgets = this.widgets as Router.WidgetContainer;
    return widgets.navbar.active;
  }

  set isNavBarVisible(value: boolean) {
    const alpha = value ? 1 : 0;
    const visible = value ? true : false;

    const widgets = this.widgets as Router.WidgetContainer;
    widgets.navbar.patch({ alpha, visible });
  }

  get topNavFocusable() {
    return this._isTopNavFocusable;
  }

  set topNavFocusable(value: boolean) {
    this._isTopNavFocusable = value;
  }

  set topNavTitle(value: string) {
    this.getTopNavWidget().patch({ pageTitleLabel: value });
  }

  set topNavVisible(visible: boolean) {
    this.getTopNavWidget().toggleVisibility(visible);
  }

  set topNavEnable(enable: boolean) {
    this._isTopNavFocusable = enable;
    this.topNavVisible = enable;
  }

  override _handleLeft(): void | boolean | undefined {
    const widgets = this.widgets as Router.WidgetContainer;
    if (!widgets.navbar.active) return false;
    Router.focusWidget(WidgetNames.NAVBAR);
  }

  override _handleUp(): void | boolean | undefined {
    if (!this.getTopNavWidget().active) return false;
    Router.focusWidget(WidgetNames.TOP_NAV);
    this._upLongPressStartTime = null;
  }

  static override _template(): Lightning.Component.Template<PageTemplateSpec> {
    return {
      Background: {
        w: PAGE_WIDTH,
        h: PAGE_HEIGHT,
        rect: true,
        color: Colors('appBackground').get(),
      },
      SponsorBackground: {
        visible: false,
      },
      LoadingSpinner: {
        type: LoadingSpinner,
        visible: false,
        x: 960,
        y: 540,
        zIndex: constants.ui.zIndex.spinnerLayer,
      },
    };
  }

  override _setup() {
    this.loaded = true;
  }

  override _active() {
    // Pages set this as needed
    this.topNavTitle = '';
    this.topNavVisible = true;

    let selected: TopNavSelectedType = null;
    if (this.pageId === PageId.SETTINGS) {
      selected = 'settings';
    }
    // TODO: If page id is signin/account

    this.getTopNavWidget().patch({ selected });

    // we handle this here instead of _inactive because when we go from one page to another
    // the first page becomes active first before the other becomes inactive
    this.updateLoginState();

    AppData?.device.addHoverStateChangedAction(
      this.id.toString(),
      this.onGlobalHoverStateChanged.bind(this),
    );
  }

  override _enable() {
    if (this.pageId == PageId.PLAYBACK || this.pageId == PageId.EPG) {
      this._ccChangeListener = tvCcEnabled => {
        if (this._handleCcChange) {
          const appCcEnabled =
            AppData?.storageService.subtitle.get() !==
            constants.closedCaption.none;
          if (tvCcEnabled !== appCcEnabled) this._handleCcChange(tvCcEnabled);
        }
      };
      this.application.on('ccChange', this._ccChangeListener);
    }
  }

  override _disable() {
    if (
      (this.pageId == PageId.PLAYBACK || this.pageId == PageId.EPG) &&
      this._ccChangeListener
    ) {
      this.application.removeListener('ccChange', this._ccChangeListener);
      this._ccChangeListener = undefined;
    }
  }

  override _getFocused(): any {
    return this;
  }

  override _unfocus(
    newFocusedComponent: Lightning.Component | undefined,
    prevFocusedComponent: Lightning.Component | undefined,
  ): void {
    // When widgets get focus, the page will lose focus and needs to update key input logic that isn't caught
    this._upLongPressStartTime = null;
    super._unfocus(newFocusedComponent, prevFocusedComponent);
  }

  override _captureUp(event?: KeyboardEvent): boolean | void {
    const canFocusTopNav =
      this.topNavFocusable && this.isEnabledWidget(WidgetNames.TOP_NAV);

    const isSimulated = isKeySimulated(event);

    if (!canFocusTopNav || isSimulated) {
      this._upLongPressStartTime = null;
      return false;
    }

    if (!this._upLongPressStartTime) {
      this._upLongPressStartTime = Date.now();
      return false;
    }

    const elapsed = Date.now() - this._upLongPressStartTime;
    const threshold = AppData?.device.isLowPerformanceModel()
      ? TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS + LOW_PERFORMANCE_LONG_PRESS_BUFFER
      : TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS;

    if (elapsed > threshold) {
      this.topNavVisible = true;
      Router.focusWidget(WidgetNames.TOP_NAV);
      this._upLongPressStartTime = null;
    }
    return true;
  }

  override _captureUpRelease(): boolean | void {
    this._upLongPressStartTime = null;

    return false;
  }

  protected updateLoginState() {
    const isLoggedIn = AppData?.loginRadiusService?.isLoggedIn();
    this.getTopNavWidget().patch({
      isLoggedIn,
    });
  }

  protected $onPageHover(target: any) {
    this.checkTopNavHoverFocus();
  }

  private onGlobalHoverStateChanged(globalHover: boolean) {
    if (globalHover) {
      this.checkTopNavHoverFocus();
    }
  }

  private checkTopNavHoverFocus() {
    const topNav = this.getTopNavWidget();
    if (topNav.hasFocus() && !topNav.isHovered) {
      Router.focusPage();
    }
  }

  getTopNavWidget() {
    const widgets = this.widgets as Router.WidgetContainer;
    return widgets.topnav;
  }

  hideBackground() {
    this._Background.patch({ alpha: 0 });
  }

  showBackground() {
    this._Background.patch({ alpha: 1 });
  }

  isEnabledWidget(widgetName: WidgetNames) {
    return !!EnabledWidgets[this.pageId].find(widget => widget === widgetName);
  }

  txLoaded() {
    this._updateSpinner();
  }

  _updateSpinner() {
    if (this.loaded) {
      this._LoadingSpinner.show = false;
    } else {
      this._LoadingSpinner.show = true;
    }
  }

  $onTileSelected(data?: Media | ContentHub, context?: SelectItemContext) {
    if (!data) return;

    onTileSelectedHandler(data, {
      viewContext: this.viewContext,
      pageId: this.pageId,
      ...context,
    });
  }

  handlePlayerExit(exitPlayer: () => void) {
    if (!AppData?.announcerService.getIsEnabled()) {
      exitPlayer();
      return;
    }
    this.fireAncestors('$announcerCancel');
    this.fireAncestors('$announce', translate('ttsPrompts.contentEnded'));

    if (this._playerExitTimeout) {
      Registry.clearTimeout(this._playerExitTimeout);
    }
    // Add delay to allow the content ended announcement to start first before navigating out.
    this._playerExitTimeout = Registry.setTimeout(() => {
      exitPlayer();
      this._playerExitTimeout = null;
    }, 250);
  }

  override _handleBack(): void | boolean {
    if (this.isNavBarVisible) {
      Router.focusWidget(WidgetNames.NAVBAR);
      return true;
    } else {
      Router.back();
    }
  }

  $onSwipeAction(direction: DirectionsType, strength: number) {
    const keyMap = AppData?.device.getKeyMapping();
    if (keyMap) {
      const keyEvent = getSimulatedKeyEventFromMap(keyMap, direction);
      document.dispatchEvent(keyEvent);
    }
  }
}
